import { ComponentType } from 'react';
import { ToolSignature } from '@andsend/services-agent-shared';
import { createGenericToolUI } from './generic-tool-ui';
import { AssistantToolUI } from '@assistant-ui/react';

/**
 * Creates a map of tool UIs for the provided tool signatures.
 * If a custom UI component is provided for a tool, it will be used.
 * Otherwise, a generic UI will be created using the tool's toolCallUi function.
 *
 * @param toolSignatures An array of tool signatures
 * @param customToolUIs A map of custom tool UI components by tool name
 * @returns An array of tool UI components
 */
export const createToolUIs = (
    toolSignatures: ToolSignature[],
    customToolUIs: Record<string, AssistantToolUI> = {}
): AssistantToolUI[] => {
    return toolSignatures
        .map((toolSignature) => {
            // If there's a custom UI for this tool, use it
            if (customToolUIs[toolSignature.name]) {
                return customToolUIs[toolSignature.name] as AssistantToolUI;
            }

            // If the tool has a toolCallUi function, create a generic UI for it
            if (toolSignature.toolCallUi) {
                return createGenericToolUI(toolSignature) as AssistantToolUI;
            }

            // Otherwise, return null (no UI for this tool)
            return null;
        })
        .filter(Boolean); // Filter out null values
};

/**
 * A component that renders all the tool UIs for the provided tool signatures.
 */
export const AutoRegisterToolUIs = ({
    toolSignatures,
    customToolUIs = {},
}: {
    toolSignatures: ToolSignature[];
    customToolUIs?: Record<string, AssistantToolUI>;
}) => {
    const toolUIs = createToolUIs(toolSignatures, customToolUIs);
    // Add any custom tool UIs that aren't associated with a tool signature
    const customToolUIKeys = Object.keys(customToolUIs);
    const toolSignatureNames = toolSignatures.map((signature) => signature.name);

    // Find custom tool UIs that aren't already included in the toolUIs array
    const additionalToolUIs = customToolUIKeys
        .filter((toolName) => !toolSignatureNames.includes(toolName))
        .map((toolName) => {
            return customToolUIs[toolName];
        })
        .filter(Boolean);

    // Combine the tool UIs from signatures with the additional custom tool UIs
    const allToolUIs = [...toolUIs, ...additionalToolUIs];
    return <>{allToolUIs.map((ToolUI, index) => (ToolUI ? <ToolUI key={`tool-ui-${index}`} /> : null))}</>;
};
